import React, { useState, useEffect } from 'react';
import { useDispatch,useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';
import { useNotification } from 'config/Notification';

import {
  fetchStandardsByCompany as fetchStandardsByCompanyService,
  fetchCompanyStandardsByCompany as fetchCompanyStandardsByCompanyService,
  fetchDashboardAssessmentReports as fetchDashboardAssessmentReportsService,
  fetchAssessmentsByUserStandard as fetchAssessmentsByUserService,
  fetchPendingAssessmentReportsCount as fetchPendingAssessmentReportsCountService,
  fetchCompanyStandardsByCompanyQuery as fetchCompanyStandardsByCompanyQueryService,
} from 'services/modules/cap/standardservice';

import {
  fetchOverAllScoreByAssessment as fetchOverAllScoreService,
  fetchOverallMainControlScores as fetchOverallMainControlScoresService
} from 'services/modules/cap/assessmentservice';

import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import Chart from "react-apexcharts";

import { Box, Card, CardContent,  Tab, Tabs, tabsClasses } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

import '../styles/cap.css';
import { setLoading } from 'stores/modules/idp/authreducer';
import Loader from 'components/Loader';

import GapAnalysisGraph from './GapAnalysis';

const DashboardPage = () => {
  const dispatch =useDispatch();
  
  // React router
  const navigate = useNavigate();

  // Notification
  const { openNotification } = useNotification();
  
  // Redux selectors
  const selectedCompanyContainer = useSelector((state) => state.auth.companyData);
  const authenticatedUserContainer = useSelector(state => state.auth.user);
  const loading = useSelector((state) => state.auth.loading);
  
  // State variables
  const [selectedStandards, setSelectedStandards] = useState([]);
  const [selectedStandard, setSelectedStandard] = useState('');
  const [series, setSeries] = useState([0, 0, 0]);
  const [assessmentId, setAssessmentId] = useState('');
  const [assessmentsList, setAssessmentsList] = useState([]);
  const [completedControls, setCompletedControls] = useState(0);
  const [pendingControls, setPendingControls] = useState(0);
  const [value, setValue] = useState(0);
  const status = [
    'Mitigation Initiated',
    'Mitigation Response In Progress',
    'Mitigation Response Submitted'
  ];
  const [controlwisescores, setControlwisescores] = useState([]);
    // US-CAP-205: subcontrols for question type coverage widget
  const [subcontrolsList, setSubcontrolsList] = useState([]);

  // Current Analysis graph 
  let chartstate = {
          
    series: series,
    options: {
      chart: {
        width: 380,
        type: 'donut',
      },
      colors: ["#ff0000", "#feb019", "#4caf50"], 
      dataLabels: {
        enabled: false
      },
      labels: ["Urgent Attention", "Needs Improvement", "Doing Well"],
      responsive: [{
        breakpoint: 480,
        options: {
          chart: {
            width: 200
          },
          legend: {
            show: false
          }
        }
      }],
      legend: {
        position: 'right',
        offsetY: 0,
        height: 200,
      }
    },
  };

  const [currentAnalysisChatOptions, setCurrentAnalysisChatOptions] = useState(chartstate);
  
  useEffect(() => {
    if (selectedCompanyContainer?.companyid !== null) {
      getCompanySelectedStandards();
      setValue(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyContainer]);
  
  // Fetches the company selected standards based on company id
  const getCompanySelectedStandards = async () => {
    try {
      setSelectedStandards([]);
      let params = { companyid: selectedCompanyContainer.companyid };
      const response = await fetchStandardsByCompanyService(params);
      if(response?.data?.length > 0)
        {
         const array1 = response?.data[0]?.selected_standards;
         const array2 = response?.data[0]?.predefined_standards;
         const companyId = selectedCompanyContainer.companyid;
         let standardarray = [];
         let group = 'Main Standard';
         const mainStandardResponse = await fetchCompanyStandardsByCompanyService({group, companyId});
         for(const standard of mainStandardResponse.data){
          if(array1.includes(standard.stdid)){
            standardarray.push(standard.name);
          }
         }
         group = 'Domain Standard';
         const domainStandardResponse = await fetchCompanyStandardsByCompanyService({group, companyId});
         for(const standard of domainStandardResponse.data){
          if(array2.includes(standard.stdid)){
            standardarray.push(standard.name);
          }
         }
         setSelectedStandards(standardarray);
         const standard = standardarray?.length===0? "" : standardarray[0];
         tabselect(standard);
        }
    } catch (error) {
      openNotification('Server error', 'error')
    }
  };
  
  // Handles change in selected standard
  const handleChange = (event, newValue) => {
    setValue((value) => value = newValue);
    tabselect(selectedStandards[newValue]);
  };

  // Based on selected standard and role it fetches the assessments data
  const tabselect = async (tabName) => {
    await getReport(tabName);
    setSelectedStandard(tabName);
    const selectedStandardDiv = document.getElementById('selectedstandarddiv');
    if (selectedStandardDiv) {
      selectedStandardDiv.innerHTML = `<h3>${tabName} Dashboard</h3>`;
    }
    if (authenticatedUserContainer && authenticatedUserContainer.roles && authenticatedUserContainer.roles[0].label === 'User') {
      getAssessmentByCompanyUserStandard(tabName);
    }
    getAssessmentControlsCountsByCompanyStandard(tabName);
  };
  
  // Fetches assessment taken by a company user of a soecific standard
  const getAssessmentByCompanyUserStandard = async (standard) => {
    setAssessmentsList([]);
    let params = { company: selectedCompanyContainer.companyid, user: authenticatedUserContainer.id, standardname: standard };
    let assessmentstandardsresponse = await fetchAssessmentsByUserService(params);
    if (assessmentstandardsresponse.data.length > 0) {
      setAssessmentsList(assessmentstandardsresponse.data);
    }
  };
  
  // Fetches the assessment control counts of the selected standard
  const getAssessmentControlsCountsByCompanyStandard = async (standard) => {
    try {
      setCompletedControls(0);
      setPendingControls(0);
      let params = { company: selectedCompanyContainer.companyid, standardname: standard };
      let response = await fetchPendingAssessmentReportsCountService(params);

      if (response?.data?.length > 0) {
        if (status.includes(response.data[0].assessments.status)) {
          setAssessmentId(response.data[0].assessments.assessment_id);
          response.data[0].assessments.mitigationresult.forEach((e) => {
            if (e.pending === 0) {
              setCompletedControls(completedControls + 1);
            } else {
              setPendingControls(pendingControls + 1);
            }
          })
        }
      }
    } catch (error) {
      openNotification(error?.response?.data?.message, 'error')
    }
  };
  
  // Handles Navigation to complete reports dashboard component
  const viewDashboard = () => {
    navigate('/cap/completereportdashboard', { state: { assessmentid: assessmentId } })
  };

  // Fetches all the completed assessment details of a specific standard and company
  const getReport = async (standard) => {
    try {
      let params = { name: standard, company:selectedCompanyContainer?.companyid };
      setControlwisescores([]);
      setSubcontrolsList([]); // US-CAP-205: reset on tab change

      const standardResponse = await fetchCompanyStandardsByCompanyQueryService({params});
      if (standardResponse?.data?.length > 0) {
        let parameters = { company: selectedCompanyContainer.companyid, standard: standardResponse.data[0]?.id, status: 'Completed' };
        let response = await fetchDashboardAssessmentReportsService(parameters);

        if (response?.data?.length > 0) {
          setAssessmentId(response.data[0]['assessment_id']);
          await getScoreByAssessmentIds(response.data[0]['assessment_id']);
        } else {
          setAssessmentId('');
          setSeries([0,0,0]); //reset the donut chart series
          setControlwisescores([]); //reset gap analysis data

          const mount = document.getElementById("tabcontentmount1");
          if (mount) {
            mount.innerHTML = "<div class='center-content'><h5>Please complete at least one Assessment to view Dashboard</h5></div>";
          }
        }
      }
    } catch (error) {
      const msg = error?.response?.data?.message || "An error occurred while loading the report";
      openNotification(msg, 'error')
    }
  };
  
  // Handles navigation to standards component
  const goToStandards = () => {
    navigate('/cap/standards')
  };
  
  // Handles navigation to self assessment component
  const goToAssessment = () => {
    navigate('/cap/selfassessment', {
      state: {
        assessmentid: assessmentsList[0].assessments.assessment_id,
        standard: assessmentsList[0].standard
      }
    })
  };
  
  // Fetches scores by assessment ids and based on overall percentage calls chart components
  const getScoreByAssessmentIds = async (assessmentId) => {
    try {
      dispatch(setLoading(true));
      let params = { assessment_id: assessmentId };
      let response = await fetchOverAllScoreService(params);
      if (response?.data?.OverAllPercentage) {
        setTimeout(() => loadMeterCharts(response.data.OverAllPercentage), 100);
        setTimeout(() => getRadardata(assessmentId), 100);
      }
    } catch (error) {
      openNotification(error?.response?.data?.message, 'error')
    }finally{
      dispatch(setLoading(false));
    }
  };
  
  // Displays the assessment details for a specific standard in form of meter charts
  const loadMeterCharts = (value) => {
    value = parseInt(value);
    const chart = am4core.create("tabcontentmount1", am4charts.GaugeChart);
    chart.innerRadius = -15;
  
    const axis = chart.xAxes.push(new am4charts.ValueAxis());
    axis.min = 0;
    axis.max = 100;
    axis.strictMinMax = true;
  
    const gradient = new am4core.LinearGradient();
    gradient.stops.push({ color: am4core.color("red") });
    gradient.stops.push({ color: am4core.color("orange") });
    gradient.stops.push({ color: am4core.color("yellow") });
    gradient.stops.push({ color: am4core.color("green") });
  
    axis.renderer.line.stroke = gradient;
    axis.renderer.line.strokeWidth = 15;
    axis.renderer.line.strokeOpacity = 1;
    axis.renderer.labels.template.fontSize = 18;
  
    axis.renderer.grid.template.disabled = true;
  
    const hand = chart.hands.push(new am4charts.ClockHand());
    hand.radius = am4core.percent(97);
    hand.value = value;
  };

  // Display the over all cumulative score in radar data form as a pointer pointing towards score out of 100
  const getRadardata = async (id) => {
    try {
      dispatch(setLoading(true));
      setAssessmentId('');
      let params = { assessment_id: id };
      let response = await fetchOverallMainControlScoresService(params);
      if (response.data) {
        let red =0;
        let orange = 0;
        let green= 0;
        response.data.OverAllscore.forEach((value) => {
          if (parseInt(value.score) >= 0 && parseInt(value.score) <= 40) {
            red += 1;
          } else if (parseInt(value.score) > 40 && parseInt(value.score) <= 75) {
            orange += 1;
          } else if (parseInt(value.score) > 75) {
            green += 1;
          }
        });
        setAssessmentId(id);
        setSeries([red, orange, green])
        setControlwisescores(response.data.OverAllscore)
      }
      else{
        setSeries([0,0,0])
      }
    } catch (error) {
      openNotification(error?.response?.data?.message, 'error')
    }finally{
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    if (assessmentId && selectedStandard && series) {
      setCurrentAnalysisChatOptions((prevOptions) => {
        const updatedOptions = {
          ...prevOptions,
          options: {
            ...prevOptions.options,
            chart: {
              ...prevOptions.options.chart,
              events: {
                dataPointSelection: (event, chartContext, config) => {
                  const clickedLabel = config.w.config.labels[config.dataPointIndex];
                  // Navigate with the selected details
                  setTimeout(() => {
                    navigate("/cap/viewcontrols", {
                      state: { assessmentid: assessmentId, standard: selectedStandard, label: clickedLabel },
                    });
                  }, 300);
                },
              },
            },
          },
          series: [...series], // Ensure the latest series is applied
        };
        return updatedOptions; // Return the updated state object
      });
    }
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId, selectedStandard, series]); // Dependencies to trigger the effect
  

  
  return (
    <div >
      {loading && (
        <div className="loader-overlay-dashboard" style={{width:'84%',left:'16%'}}>
          <Loader/>
        </div>
      )}
      <div className="row py-2">
      </div>
      
      {selectedStandards.length <= 0 && (
        <div className="dived-card cell-items">
          <Card className="d-block  py-3 cap-dashboard-container" >
            <CardContent>
              <h5 className="rating-value">
                <InfoIcon className='cap-info-icon' /> <br /><br />
                <div> Welcome to the CAP dashboard!</div>
                <div>Unlock user dashboards: Assign standards now!</div>
              </h5>
            </CardContent>
          </Card>
        </div>
      )}
      {selectedStandards && selectedStandards.length > 0 && (
        <Box className="cap-tabs-section"
          sx={{
            flexGrow: 1,
            bgcolor: 'background.paper',
            width: 'auto'
          }}
        >
          <Tabs className="cap-tabs"
            value={value}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons
            sx={{
              [`& .${tabsClasses.scrollButtons}`]: {
                '&.Mui-disabled': { opacity: 0.3 },
              },
            }}
          >
            {selectedStandards.map((tab, index) => (
              <Tab className='mx-1'
                key={index}
                label={
                  <span>{tab}</span>
                }
              />
            ))}
          </Tabs>
        </Box>
      )}
      {authenticatedUserContainer &&
        authenticatedUserContainer.roles &&
        (authenticatedUserContainer.roles[0].permissions?.includes('CREATE_SELF_ASSESSMENT')) && (
          <div className="text-right">
            <a className='text-primary forgottext right-align' href onClick={() => goToStandards()}>Manage your standards</a>
          </div>
        )}
      {selectedStandards && selectedStandards.length > 0 && (
        <>
          <div className="row hundred">
            <div className="col-lg-12" id="selectedstandarddiv">
            </div>
            <div className="col-lg-6">
              <div className="card shadow mb-2">
                <div className="card-header py-2">
                  <h6 className="m-0 font-weight-bold text-primary">Risk status</h6>
                </div>
                <div className="card-body" id="tabcontentmount1">
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="card shadow mb-2">
                <div className="card-header py-2">
                  <h6 className="m-0 font-weight-bold text-primary">Control wise scorecard</h6>
                </div>
                 
                <div className="card-body" id="tabcontentmount3">
                  { assessmentId && currentAnalysisChatOptions?.series?.length > 0 ? (
                    <Chart
                      options={currentAnalysisChatOptions.options}
                      series={currentAnalysisChatOptions.series}
                      type="donut"
                      height="250"
                    />
                  ) : (
                    <div className="center-content">
                      <h5>Please complete at least one Assessment to view Dashboard</h5>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
          </div>
          <div className='row hundred'>
            <div className="col-lg-12">
              <GapAnalysisGraph data={controlwisescores} />
            </div>
            <div className="col-lg-12" >
              {assessmentsList.length > 0 && assessmentsList[0].assessments.status === 'Open' && (
                <button
                  onClick={goToAssessment} type='button' className='approve-button py-sm px-xl ml-sm my-xs'>
                  <span >Start assessment</span>
                </button>
              )}
              {assessmentsList.length > 0 && assessmentsList[0].assessments.status === 'Work In Progress' && (
                <button
                  color='primary' onClick={goToAssessment} type='button' className='approve-button py-sm px-xl ml-sm my-xs'>
                  <span >Resume assessment</span>
                </button>
              )}
              {assessmentId !== '' && (
                <button className="view-dashboard-btn approve-button py-sm px-xl ml-sm my-xs" onClick={viewDashboard} type='button'>View Complete Dashboard</button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
  }
  
  export default DashboardPage;
  
